/**
 * GamePopover (web) — the card a game in the ticker opens on hover, keyboard
 * focus or tap: matchup, start time or live score, the best cached Moneyline /
 * Spread / Total per side, and actions (search, cached SGPs, live odds).
 *
 * Mirrors InjuryPopover: a portal into document.body (the ticker row clips),
 * placed under the game and clamped to the viewport, flipping above when
 * there's no room. The tickers stay dark, so the card does too. Unlike the
 * injury card it holds buttons, so it is a non-modal dialog the ticker moves
 * keyboard focus into (Tab from the game).
 *
 * Prices come from the odds cache (services/gameLines); the live feed is a
 * sandbox (+100 everywhere), so the card shows price and book only, never EV.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { View, Text, Pressable, StyleSheet, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useReducedMotion } from '../utils/motion';
import { getTeamLogo } from '../data/logos';
import { sportOf } from '../services/gameLines';
import { TeamLogo, shortTeamLabel } from './gameTickerParts';

const WIDTH = 344;
const MARGIN = 12;
const GAP = 8;
const EST_HEIGHT = 330;
const FONT = 'Slate, -apple-system, system-ui, sans-serif';
const VIOLET = '#c4b5fd';

/** Viewport placement under the game, clamped to the screen; flips above when needed. */
export function placeGameCard(rect, viewport, height = EST_HEIGHT) {
    const width = Math.min(WIDTH, viewport.width - MARGIN * 2);
    const center = rect.left + rect.width / 2;
    const left = Math.max(MARGIN, Math.min(center - width / 2, viewport.width - width - MARGIN));
    const below = rect.bottom + GAP;
    const flip = below + height > viewport.height && rect.top - GAP - height > MARGIN;
    const top = flip ? rect.top - GAP - height : below;
    const arrowX = Math.max(16, Math.min(center - left, width - 16));
    return { left, top, width, arrowX, flip };
}

export const formatAmerican = (p) => (typeof p === 'number' && Number.isFinite(p) ? (p > 0 ? `+${p}` : `${p}`) : '—');
const formatSpread = (n) => (n > 0 ? `+${n}` : `${n}`);

/** Slow opacity pulse; still under reduced motion. */
function usePulse(active, reduced) {
    const v = useRef(new Animated.Value(1)).current;
    useEffect(() => {
        if (!active || reduced) { v.setValue(1); return undefined; }
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(v, { toValue: 0.45, duration: 700, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
            Animated.timing(v, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
        ]));
        loop.start();
        return () => loop.stop();
    }, [active, reduced, v]);
    return v;
}

function LineCell({ side, sideLabel, loading, pulse }) {
    if (loading) {
        return (
            <Animated.View style={[s.cell, { opacity: pulse }]}>
                <View style={[s.skel, { width: 58 }]} />
                <View style={[s.skel, s.skelSmall, { width: 44 }]} />
            </Animated.View>
        );
    }
    if (!side) {
        return (
            <View style={s.cell}>
                <Text style={s.cellMissing} aria-hidden>—</Text>
                <Text style={s.srOnly}>No cached line</Text>
                <Text style={s.cellBook} aria-hidden> </Text>
            </View>
        );
    }
    return (
        <View style={s.cell}>
            <View style={s.cellTop}>
                {sideLabel ? <Text style={s.cellLine} numberOfLines={1}>{sideLabel}</Text> : null}
                <Text style={s.cellPrice}>{formatAmerican(side.price)}</Text>
            </View>
            <Text style={s.cellBook} numberOfLines={1}>{side.book}</Text>
        </View>
    );
}

function LineRow({ label, testID, cells, loading, pulse, shown, reduced, index }) {
    return (
        <View
            testID={testID}
            style={[
                s.lineRow,
                index > 0 && s.lineRowRule,
                {
                    opacity: shown || reduced ? 1 : 0,
                    transform: [{ translateY: shown || reduced ? 0 : 3 }],
                    transitionProperty: 'opacity, transform',
                    transitionDuration: reduced ? '0ms' : '220ms',
                    transitionDelay: reduced ? '0ms' : `${index * 50}ms`,
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                },
            ]}
        >
            <Text style={s.lineLabel}>{label}</Text>
            {cells.map((c) => (
                <LineCell key={c.key} side={c.side} sideLabel={c.label} loading={loading} pulse={pulse} />
            ))}
        </View>
    );
}

function ActionButton({ label, icon, tone = 'secondary', onPress, buttonRef, testID }) {
    const primary = tone === 'primary';
    const live = tone === 'live';
    return (
        <Pressable
            ref={buttonRef}
            testID={testID}
            role="button"
            aria-label={label}
            dataSet={{ pressable: 'scale' }}
            onPress={onPress}
            style={({ hovered, focused, pressed }) => [
                s.btn,
                primary ? s.btnPrimary : live ? s.btnLive : s.btnSecondary,
                hovered && (primary ? s.btnPrimaryHover : live ? s.btnLiveHover : s.btnSecondaryHover),
                focused && s.btnFocus,
                pressed && { transform: [{ scale: 0.97 }] },
            ]}
        >
            {live ? <View style={s.liveDot} /> : <Ionicons name={icon} size={13} color={primary ? '#fff' : VIOLET} />}
            <Text style={[s.btnText, primary ? s.btnTextPrimary : live ? s.btnTextLive : s.btnTextSecondary]} numberOfLines={1}>{label}</Text>
            {primary ? <Ionicons name="arrow-forward" size={13} color="rgba(255,255,255,0.85)" /> : null}
        </Pressable>
    );
}

export default function GamePopover({
    id, open, fixture, anchorRect, status, timeLabel, liveScore, periodLabel, lines,
    showSGP = false, showLive = false, onSearch, onBuildSGP, onLiveOdds,
    onMouseEnter, onMouseLeave, onFocus, onBlur, onKeyDown, cardRef, firstActionRef,
}) {
    const reduced = useReducedMotion();
    const [shown, setShown] = useState(false);
    const fixtureId = fixture ? String(fixture.fixture_id || fixture.id || '') : '';
    useEffect(() => {
        if (!open) { setShown(false); return undefined; }
        if (reduced) { setShown(true); return undefined; }
        setShown(false);
        const raf = requestAnimationFrame(() => setShown(true));
        return () => cancelAnimationFrame(raf);
    }, [open, reduced, fixtureId]);

    const loading = !lines || lines.status === 'loading' || lines.status === 'idle';
    const [rowsShown, setRowsShown] = useState(false);
    useEffect(() => {
        if (loading) { setRowsShown(true); return undefined; } // skeletons sit still, no entry
        if (reduced) { setRowsShown(true); return undefined; }
        setRowsShown(false);
        const raf = requestAnimationFrame(() => setRowsShown(true));
        return () => cancelAnimationFrame(raf);
    }, [loading, reduced, fixtureId]);

    const isLive = status?.status === 'live';
    const pulse = usePulse(open && loading, reduced);
    const liveDot = usePulse(open && isLive, reduced);

    if (!open || !fixture || !anchorRect || typeof document === 'undefined') return null;

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const pos = placeGameCard(anchorRect, viewport);
    const away = fixture.away_team || 'TBD';
    const home = fixture.home_team || 'TBD';
    const sport = sportOf(fixture);
    const awayLogo = fixture.away_logo || fixture.away_team_logo || getTeamLogo(away, sport);
    const homeLogo = fixture.home_logo || fixture.home_team_logo || getTeamLogo(home, sport);
    const awayShort = shortTeamLabel(away, fixture.league);
    const homeShort = shortTeamLabel(home, fixture.league);
    const hasScore = !!(liveScore && liveScore.is_live && liveScore.away_score !== null && liveScore.away_score !== undefined);
    const v = lines?.value || null;
    const empty = !loading && (!v || [v.moneyline?.away, v.moneyline?.home, v.spread?.away, v.spread?.home, v.total?.over, v.total?.under].every((x) => !x));
    const accent = status?.color || '#3B82F6';

    const rows = [
        {
            key: 'moneyline', label: 'Moneyline',
            cells: [{ key: 'a', side: v?.moneyline?.away }, { key: 'h', side: v?.moneyline?.home }],
        },
        {
            key: 'spread', label: 'Spread',
            cells: [
                { key: 'a', side: v?.spread?.away, label: v?.spread?.away ? formatSpread(v.spread.away.line) : null },
                { key: 'h', side: v?.spread?.home, label: v?.spread?.home ? formatSpread(v.spread.home.line) : null },
            ],
        },
        {
            key: 'total', label: 'Total',
            cells: [
                { key: 'o', side: v?.total?.over, label: v?.total?.over ? `O ${v.total.over.line}` : null },
                { key: 'u', side: v?.total?.under, label: v?.total?.under ? `U ${v.total.under.line}` : null },
            ],
        },
    ];

    const node = (
        <View
            ref={cardRef}
            id={id}
            role="dialog"
            aria-label={`${away} at ${home}: best lines and actions`}
            style={[
                s.card,
                { left: pos.left, top: pos.top, width: pos.width },
                {
                    opacity: shown ? 1 : 0,
                    transform: [{ translateY: shown || reduced ? 0 : (pos.flip ? 4 : -4) }, { scale: shown || reduced ? 1 : 0.98 }],
                    transitionProperty: 'opacity, transform',
                    transitionDuration: reduced ? '0ms' : '170ms',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                },
            ]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        >
            <View style={[s.accent, { backgroundColor: accent }]} />
            <View style={[s.arrow, pos.flip ? s.arrowBottom : s.arrowTop, { left: pos.arrowX - 6 }]} />

            <View style={{ maxHeight: Math.max(160, viewport.height - pos.top - MARGIN - 8), overflowY: 'auto' }}>
                {/* ── Status line ── */}
                <View style={s.metaRow}>
                    {fixture.league ? (
                        <View style={s.leaguePill}><Text style={s.leaguePillText}>{String(fixture.league).toUpperCase()}</Text></View>
                    ) : null}
                    <View style={[s.statusPill, { backgroundColor: `${accent}1f`, borderColor: `${accent}55` }]}>
                        {isLive ? <Animated.View style={[s.statusDot, { backgroundColor: accent, opacity: liveDot }]} /> : null}
                        <Text style={[s.statusText, { color: accent }]}>{status?.label || 'UPCOMING'}</Text>
                    </View>
                    <Text style={s.metaTime} numberOfLines={1}>
                        {hasScore ? (periodLabel || 'In progress') : (isLive ? 'In progress' : timeLabel)}
                    </Text>
                </View>

                {/* ── Matchup: away @ home, scoreboard style ── */}
                <View style={s.teams}>
                    <View style={s.teamRow}>
                        <TeamLogo logoUrl={awayLogo} teamName={away} size={22} />
                        <Text style={s.teamName} numberOfLines={1}>{away}</Text>
                        {hasScore ? <Text style={s.score}>{liveScore.away_score}</Text> : <Text style={s.teamSide}>AWAY</Text>}
                    </View>
                    <View style={s.teamRow}>
                        <TeamLogo logoUrl={homeLogo} teamName={home} size={22} />
                        <Text style={s.teamName} numberOfLines={1}><Text style={s.at}>@ </Text>{home}</Text>
                        {hasScore ? <Text style={s.score}>{liveScore.home_score}</Text> : <Text style={s.teamSide}>HOME</Text>}
                    </View>
                </View>

                {/* ── Best lines ── */}
                <View style={s.board} aria-busy={loading || undefined}>
                    <View style={s.boardHead}>
                        <Text style={s.boardTitle}>{isLive ? 'PRE-GAME' : 'BEST LINES'}</Text>
                        <Text style={s.colHead} numberOfLines={1}>{awayShort}</Text>
                        <Text style={s.colHead} numberOfLines={1}>{homeShort}</Text>
                    </View>
                    {rows.map((r, i) => (
                        <LineRow
                            key={r.key}
                            testID={`game-line-${r.key}`}
                            label={r.label}
                            cells={r.cells}
                            loading={loading}
                            pulse={pulse}
                            shown={rowsShown}
                            reduced={reduced}
                            index={i}
                        />
                    ))}
                    <Text style={s.footnote}>
                        {empty
                            ? (lines?.status === 'error' ? 'Lines unavailable right now' : 'No cached lines for this game yet')
                            : isLive
                                ? 'Pre-game best prices from the cache. Live odds has in-play prices.'
                                : 'Best price per side across cached books'}
                    </Text>
                </View>

                {/* ── Actions ── */}
                <View style={s.actions}>
                    <ActionButton label="Search this game" icon="search" tone="primary" onPress={onSearch} buttonRef={firstActionRef} testID="game-card-search" />
                    {showSGP ? <ActionButton label="Build SGP" icon="layers-outline" onPress={onBuildSGP} testID="game-card-sgp" /> : null}
                    {showLive ? <ActionButton label="Live odds" tone="live" onPress={onLiveOdds} testID="game-card-live" /> : null}
                </View>
            </View>
        </View>
    );

    return createPortal(node, document.body);
}

const s = StyleSheet.create({
    card: {
        position: 'fixed',
        zIndex: 100000,
        paddingTop: 14,
        paddingBottom: 12,
        paddingHorizontal: 14,
        borderRadius: 14,
        backgroundColor: 'rgba(18,18,24,0.98)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.09)',
        boxShadow: '0 18px 48px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35)',
        overflow: 'visible',
    },
    accent: {
        position: 'absolute', top: 0, left: 14, right: 14, height: 2,
        borderBottomLeftRadius: 2, borderBottomRightRadius: 2, opacity: 0.85,
    },
    arrow: {
        position: 'absolute', width: 12, height: 12,
        backgroundColor: 'rgba(18,18,24,0.98)', borderColor: 'rgba(255,255,255,0.09)',
        transform: [{ rotate: '45deg' }],
    },
    arrowTop: { top: -6.5, borderTopWidth: 1, borderLeftWidth: 1 },
    arrowBottom: { bottom: -6.5, borderBottomWidth: 1, borderRightWidth: 1 },

    metaRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    leaguePill: {
        paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
        backgroundColor: 'rgba(196,181,253,0.12)', borderWidth: 1, borderColor: 'rgba(196,181,253,0.25)',
    },
    leaguePillText: { color: VIOLET, fontSize: 9.5, fontWeight: '800', letterSpacing: 0.6, fontFamily: FONT },
    statusPill: {
        flexDirection: 'row', alignItems: 'center', gap: 5,
        paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, borderWidth: 1,
    },
    statusDot: { width: 6, height: 6, borderRadius: 3 },
    statusText: { fontSize: 9.5, fontWeight: '800', letterSpacing: 0.6, fontFamily: FONT },
    metaTime: { flex: 1, textAlign: 'right', color: 'rgba(255,255,255,0.55)', fontSize: 11.5, fontWeight: '600', fontFamily: FONT, fontVariant: ['tabular-nums'] },

    teams: { marginTop: 12, gap: 8 },
    teamRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    teamName: { flex: 1, minWidth: 0, color: '#fff', fontSize: 15, lineHeight: 19, fontWeight: '700', letterSpacing: -0.2, fontFamily: FONT },
    at: { color: 'rgba(255,255,255,0.35)', fontWeight: '600' },
    teamSide: { color: 'rgba(255,255,255,0.45)', fontSize: 9, fontWeight: '700', letterSpacing: 0.8, fontFamily: FONT },
    score: { color: '#fff', fontSize: 18, fontWeight: '800', fontFamily: FONT, fontVariant: ['tabular-nums'], minWidth: 28, textAlign: 'right' },

    board: {
        marginTop: 14, paddingTop: 10, paddingBottom: 8, paddingHorizontal: 10,
        borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.03)',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)',
    },
    boardHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
    boardTitle: { width: 76, color: 'rgba(255,255,255,0.55)', fontSize: 9, fontWeight: '800', letterSpacing: 1, fontFamily: FONT },
    colHead: { flex: 1, minWidth: 0, color: 'rgba(255,255,255,0.5)', fontSize: 9.5, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: FONT },
    lineRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 7 },
    lineRowRule: { borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)' },
    lineLabel: { width: 76, color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: '600', fontFamily: FONT },
    cell: { flex: 1, minWidth: 0, gap: 2 },
    cellTop: { flexDirection: 'row', alignItems: 'baseline', gap: 5 },
    cellLine: { color: 'rgba(255,255,255,0.6)', fontSize: 11.5, fontWeight: '600', fontFamily: FONT, fontVariant: ['tabular-nums'] },
    cellPrice: { color: '#fff', fontSize: 13.5, fontWeight: '800', fontFamily: FONT, fontVariant: ['tabular-nums'] },
    cellBook: { color: 'rgba(255,255,255,0.6)', fontSize: 10.5, fontWeight: '500', fontFamily: FONT },
    cellMissing: { color: 'rgba(255,255,255,0.3)', fontSize: 13.5, fontWeight: '600', fontFamily: FONT },
    skel: { height: 11, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)' },
    skelSmall: { height: 8, marginTop: 3 },
    footnote: { marginTop: 4, color: 'rgba(255,255,255,0.55)', fontSize: 10, fontWeight: '500', fontFamily: FONT },

    actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
    btn: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
        height: 32, paddingHorizontal: 11, borderRadius: 8, borderWidth: 1,
        cursor: 'pointer', outlineStyle: 'none',
        transitionProperty: 'background-color, border-color, box-shadow, transform',
        transitionDuration: '140ms',
    },
    btnPrimary: { flexGrow: 1, backgroundColor: '#7c3aed', borderColor: '#8b5cf6' },
    btnPrimaryHover: { backgroundColor: '#8b5cf6' },
    btnSecondary: { backgroundColor: 'rgba(196,181,253,0.08)', borderColor: 'rgba(196,181,253,0.28)' },
    btnSecondaryHover: { backgroundColor: 'rgba(196,181,253,0.16)', borderColor: 'rgba(196,181,253,0.45)' },
    btnLive: { backgroundColor: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.35)' },
    btnLiveHover: { backgroundColor: 'rgba(16,185,129,0.16)', borderColor: 'rgba(16,185,129,0.55)' },
    btnFocus: { boxShadow: '0 0 0 2px rgba(18,18,24,1), 0 0 0 4px #a78bfa' },
    btnText: { fontSize: 12.5, fontWeight: '700', fontFamily: FONT },
    btnTextPrimary: { color: '#fff' },
    btnTextSecondary: { color: VIOLET },
    btnTextLive: { color: '#34d399' },
    liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#10B981' },
    srOnly: {
        position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', borderWidth: 0,
    },
});
